import { useMutation, useQueryClient } from '@tanstack/react-query';
import supabaseClient from '@/api/supabaseClient.ts';
import { Database } from '@/_constants/database.types';

type QuestionCategory = Database['public']['Enums']['question_types'];

interface CreatePracticeSetParams {
  authId: string;
  questionCategory?: QuestionCategory;
  categoryCode?: string; // Optional: specific category code for paper-specific filtering (e.g., 'LANG-P2-Q1')
  setSize?: number;
  retryQuestionId?: string; // Optional: specific question to retry
  retryPracticeSetId?: string; // Optional: practice set to retry (retakes same questions)
  hasImages?: boolean; // Optional: filter Section B questions by image presence
}

interface CreatePracticeSetResponse {
  practice_set_id: string;
  question_count: number;
}

const useCreatePracticeSet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      authId,
      questionCategory,
      categoryCode,
      setSize,
      retryQuestionId,
      retryPracticeSetId,
      hasImages,
    }: CreatePracticeSetParams): Promise<CreatePracticeSetResponse> => {
      const response = await supabaseClient.functions.invoke('create-practice-set', {
        body: {
          auth_id: authId,
          question_category: questionCategory,
          category_code: categoryCode,
          set_size: setSize,
          retry_question_id: retryQuestionId,
          retry_practice_set_id: retryPracticeSetId,
          has_images: hasImages,
        },
      });

      if (response.error) {
        // Try to extract error message from response data (for non-2xx responses)
        const errorMessage = (response.data as any)?.errors?.[0] || response.error.message;
        if (response.error?.context?.status === 429) {
          throw new Error("You've reached your daily limit of 25 questions. Your limit will reset at midnight.");
        }
        throw new Error(errorMessage);
      }

      return response.data?.data;
    },
    onSuccess: () => {
      // Invalidate practice sets list to refetch
      queryClient.invalidateQueries({ queryKey: ['practice_attempts'] });
    },
  });
};

export default useCreatePracticeSet;
